import { Button as AntButton } from 'antd';
import styled, { css } from 'styled-components';
import { colors } from '@10d/tend-ui-tokens/samolet';

import { ButtonProps } from './types';

export const activeState = css`
  background: ${colors.gray0};
  border-color: ${colors.blue700};
  color: ${colors.blue700};
`;

export const hoverState = css`
  background: ${colors.gray0};
  border-color: ${colors.blue500};
  color: ${colors.blue500};
`;

export const defaultState = css`
  border-color: #bdc5db;
  color: ${colors.gray900};
`;

const defaultMode = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 4px 20px;
  font-size: 14px;
  ${defaultState}

  &:hover,
  &:focus {
    ${hoverState}
  }

  &:active {
    ${activeState}
  }

  &.ant-btn-lg {
    padding: 8px 24px;
    line-height: 24px;
  }

  &.ant-btn-sm {
    padding: 0px 20px;
  }

  &.ant-btn-icon-only {
    border-radius: 100%;
    padding: 0;
  }

  .anticon {
    font-size: 16px;
  }

  &:not(.ant-btn-icon-only) .anticon + span,
  &:not(.ant-btn-icon-only) .ant-btn-loading-icon + span {
    margin-left: 4px;
  }

  .ant-btn-loading-icon .anticon {
    padding-right: 0;
  }

  .anticon,
  .ant-btn-loading-icon {
    display: flex;
  }

  &,
  && {
    &[disabled],
    &[disabled]:hover,
    &[disabled]:focus,
    &[disabled]:active {
      color: ${colors.gray400};
      background: #eaedf5;
      border-color: #bdc5db;
    }
  }

  & > .ant-btn-loading-icon .anticon {
    animation: none;

    & svg {
      animation: loadingCircle 1s infinite linear;
    }
  }
`;

const primaryMode = css`
  &.ant-btn-primary:not([disabled]) {
    color: ${colors.gray0};
    background: ${colors.blue600};
    border-color: ${colors.blue600};

    &:hover,
    &:focus {
      background: ${colors.blue500};
      border-color: ${colors.blue500};
    }

    &:active {
      background: ${colors.blue700};
      border-color: ${colors.blue700};
    }
  }
`;

const linkMode = css`
  &.ant-btn-link:not([disabled]) {
    color: ${colors.blue600};
    border: none;
    background: transparent;

    &:hover,
    &:focus {
      color: ${colors.blue500};
    }

    &:active {
      color: ${colors.blue700};
    }
  }

  &.ant-btn-link[disabled],
  &.ant-btn-link[disabled]:hover,
  &.ant-btn-link[disabled]:focus,
  &.ant-btn-link[disabled]:active {
    color: '#AEB8D0';
    border: none;
    background: transparent;
  }
`;

const dangerDefaultMode = css`
  &.ant-btn-dangerous:not([disabled]) {
    color: ${colors.red600};
    background: ${colors.gray0};

    &:hover,
    &:focus {
      color: ${colors.red500};
      border-color: ${colors.red500};
    }

    &:active {
      color: ${colors.red700};
      border-color: ${colors.red700};
    }
  }
`;

const dangerPrimaryMode = css`
  &.ant-btn-dangerous.ant-btn-primary:not([disabled]) {
    color: ${colors.gray0};
    background: ${colors.red600};
    border-color: ${colors.red600};

    &:hover,
    &:focus {
      border-color: ${colors.red500};
      background: ${colors.red500};
    }

    &:active {
      border-color: ${colors.red700};
      background: ${colors.red700};
    }
  }
`;

const dangerLinkMode = css`
  &.ant-btn-dangerous.ant-btn-link:not([disabled]) {
    color: ${colors.red600};

    &:hover,
    &:focus {
      color: ${colors.red500};
    }

    &:active {
      color: ${colors.red700};
    }
  }
`;

const excelDefaultMode = css`
  ${({ excel }: ButtonProps) =>
    excel &&
    `
    background: ${colors.gray0};
    color: ${colors.green600};
    border-color: ${colors.green600};

    &:hover,
    &:focus {
      color: ${colors.green500};
      border-color: ${colors.green500};
    }

    &:active {
      color: ${colors.green700};
      border-color: ${colors.green700};
    }`}
`;

const excelPrimaryMode = css`
  ${({ excel, type }: ButtonProps) =>
    excel &&
    type === 'primary' &&
    `
    &.ant-btn-primary:not([disabled]) {
      color: ${colors.gray0};
      background: ${colors.green600};
      border-color: ${colors.green600};

      &:hover,
      &:focus {
        border-color: ${colors.green500};
        background: ${colors.green500};
      }

      &:active {
        border-color: ${colors.green700};
        background: ${colors.green700};
      }
    }`}
`;

const excelLinkMode = css`
  ${({ excel, type }: ButtonProps) =>
    excel &&
    type === 'link' &&
    `
    &.ant-btn-link:not([disabled]) {
      color: ${colors.green600};

      &:hover,
      &:focus {
        color: ${colors.green500};
      }

      &:active {
        color: ${colors.green700};
      }
  }`}
`;

const Button = styled(AntButton)`
  ${defaultMode}
  ${primaryMode}
  ${linkMode}
  ${dangerDefaultMode}
  ${dangerPrimaryMode}
  ${dangerLinkMode}
  ${excelDefaultMode}
  ${excelPrimaryMode}
  ${excelLinkMode}
`;

export const buttonStyle = css`
  .ant-btn {
    ${defaultMode}
    ${primaryMode}
    ${linkMode}
    ${dangerDefaultMode}
    ${dangerPrimaryMode}
    ${dangerLinkMode}
  }
`;

export default Button;
